import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend as RechartsLegend, ResponsiveContainer,
} from 'recharts';
import { Loader2, Target, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { CustomMonthPicker } from '../ui/custom-month-picker';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;
const WINDOW_START = '2026-02';
const WINDOW_END = '2031-01';

const fmtTons = (v) =>
  v == null ? '—' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`;

const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v}%`);

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
};

const varianceColor = (pct) => {
  if (pct == null) return 'text-slate-400';
  if (pct >= 100) return 'text-emerald-600';
  if (pct >= 80) return 'text-amber-600';
  return 'text-red-600';
};

const varianceBg = (pct) => {
  if (pct == null) return 'bg-slate-50';
  if (pct >= 100) return 'bg-emerald-50';
  if (pct >= 80) return 'bg-amber-50';
  return 'bg-red-50';
};

// Add N months to a YYYY-MM string, clamped within window. N can be negative.
const addMonths = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  const out = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  if (out < WINDOW_START) return WINDOW_START;
  if (out > WINDOW_END) return WINDOW_END;
  return out;
};

// Compute "default" range: last 3 months including current month, clamped to window.
const computeDefaultRange = () => {
  const now = new Date();
  const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  // From = currentYm - 2 months
  const fromDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const fromYm = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}`;
  return {
    from_month: fromYm < WINDOW_START ? WINDOW_START : fromYm,
    to_month: currentYm > WINDOW_END ? WINDOW_END : currentYm,
  };
};

function MonthlyTargetReport() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('range'); // 'single' | 'range'
  const [filter, setFilter] = useState(() => computeDefaultRange());

  const fetchSummary = async (range) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (range.from_month) params.set('from_month', range.from_month);
      if (range.to_month) params.set('to_month', range.to_month);
      const { data } = await axios.get(`${API}/silica-targets/summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load monthly targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    if (mode === 'single') {
      const effective = { from_month: filter.from_month, to_month: filter.from_month };
      setFilter(effective);
      fetchSummary(effective);
      return;
    }
    // Range mode — From and To must NOT be the same and From must be earlier.
    if (filter.from_month === filter.to_month) {
      toast.error('"From" and "To" months cannot be the same. Use Single mode for a single month.');
      return;
    }
    if (filter.from_month > filter.to_month) {
      toast.error('"From" month must be before "To" month');
      return;
    }
    fetchSummary(filter);
  };

  const handleResetFilter = () => {
    const def = computeDefaultRange();
    setMode('range');
    setFilter(def);
    fetchSummary(def);
  };

  // When user switches to Range mode and From == To, auto-bump To by one month
  // (or pull From back if To is already at the window end) so the two pickers
  // can never sit on the same value.
  const handleModeChange = (next) => {
    setMode(next);
    if (next === 'range' && filter.from_month === filter.to_month) {
      const bumpedTo = addMonths(filter.to_month, 1);
      if (bumpedTo !== filter.to_month) {
        setFilter((f) => ({ ...f, to_month: bumpedTo }));
      } else {
        // To is already at window end → pull From back instead
        setFilter((f) => ({ ...f, from_month: addMonths(f.from_month, -1) }));
      }
    }
  };

  const rows = summary?.rows || [];
  const currentMonth = summary?.current_month;
  // KPI focus: current month if it falls within the range, else the last row in the range.
  const focusRow = useMemo(() => {
    if (!rows.length) return null;
    const cur = rows.find((r) => r.year_month === currentMonth);
    if (cur) return cur;
    return rows[rows.length - 1];
  }, [rows, currentMonth]);

  const chartData = rows.map((r) => ({
    name: monthLabel(r.year_month),
    Target: r.target_tons ?? null,
    Actual: r.actual_tons ?? 0,
  }));

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monthly-target-report">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className={`flex-1 grid grid-cols-1 ${mode === 'range' ? 'sm:grid-cols-2' : ''} gap-3`}>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                {mode === 'single' ? 'Month' : 'From Month'}
              </label>
              <CustomMonthPicker
                value={filter.from_month}
                onChange={(v) => setFilter((f) => ({ ...f, from_month: v }))}
                min={WINDOW_START}
                max={mode === 'range' ? addMonths(filter.to_month, -1) : WINDOW_END}
                placeholder={mode === 'single' ? 'Select month' : 'From month'}
                data-testid="filter-from-month"
              />
            </div>
            {mode === 'range' && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">To Month</label>
                <CustomMonthPicker
                  value={filter.to_month}
                  onChange={(v) => setFilter((f) => ({ ...f, to_month: v }))}
                  min={addMonths(filter.from_month, 1)}
                  max={WINDOW_END}
                  placeholder="To month"
                  data-testid="filter-to-month"
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={handleApplyFilter}
              className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              disabled={loading}
              data-testid="filter-apply-btn"
            >
              Apply
            </button>
            {/* Single / Range toggle */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="filter-mode-toggle">
              <button
                type="button"
                onClick={() => handleModeChange('single')}
                className={`h-10 px-3 text-sm font-medium transition-colors ${
                  mode === 'single'
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="filter-mode-single"
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('range')}
                className={`h-10 px-3 text-sm font-medium transition-colors border-l border-slate-200 ${
                  mode === 'range'
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="filter-mode-range"
              >
                Range
              </button>
            </div>
            <button
              onClick={handleResetFilter}
              className="h-10 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
              data-testid="filter-reset-btn"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI tiles (single month → that month; range → current month if in range, else last) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          icon={<Target className="w-4 h-4" />}
          label={`Target (${focusRow ? monthLabel(focusRow.year_month) : '—'})`}
          value={fmtTons(focusRow?.target_tons)}
          accent="slate"
          testid="kpi-target"
        />
        <KpiTile
          icon={<Activity className="w-4 h-4" />}
          label={`Actual (${focusRow ? monthLabel(focusRow.year_month) : '—'})`}
          value={fmtTons(focusRow?.actual_tons)}
          accent="blue"
          testid="kpi-actual"
        />
        <KpiTile
          icon={
            (focusRow?.variance_tons ?? 0) >= 0
              ? <TrendingUp className="w-4 h-4" />
              : <TrendingDown className="w-4 h-4" />
          }
          label="Variance"
          value={
            focusRow?.variance_tons == null
              ? '—'
              : `${focusRow.variance_tons >= 0 ? '+' : ''}${fmtTons(focusRow.variance_tons)}`
          }
          accent={
            focusRow?.variance_tons == null
              ? 'slate'
              : focusRow.variance_tons >= 0
              ? 'emerald'
              : 'red'
          }
          testid="kpi-variance"
        />
        <KpiTile
          icon={<TrendingUp className="w-4 h-4" />}
          label="% Achieved"
          value={fmtPct(focusRow?.variance_pct)}
          accent={
            focusRow?.variance_pct == null
              ? 'slate'
              : focusRow.variance_pct >= 100
              ? 'emerald'
              : focusRow.variance_pct >= 80
              ? 'amber'
              : 'red'
          }
          testid="kpi-pct"
        />
      </div>

      {/* Smooth-curve line chart: Target vs Actual */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Target vs Actual (tons)</h3>
          <p className="text-xs text-slate-500">
            {filter.from_month} → {filter.to_month} ({rows.length} months)
          </p>
        </div>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v, key) => [fmtTons(v), key]}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <RechartsLegend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="Target"
                stroke="#0ea5e9"
                strokeWidth={3}
                dot={{ r: 4, fill: '#0ea5e9' }}
                activeDot={{ r: 6 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="Actual"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ r: 4, fill: '#10b981' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Read-only month-by-month table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Month-by-Month</h3>
          <div className="flex items-center gap-3 text-xs">
            <LegendDot dot="bg-emerald-500" label="≥100% target" />
            <LegendDot dot="bg-amber-500" label="80–99%" />
            <LegendDot dot="bg-red-500" label="<80%" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="targets-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Month</th>
                <th className="text-right px-4 py-2 font-medium">Target (t)</th>
                <th className="text-right px-4 py-2 font-medium">Actual (t)</th>
                <th className="text-right px-4 py-2 font-medium">Variance (t)</th>
                <th className="text-right px-4 py-2 font-medium">% Achieved</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isCurrent = r.year_month === currentMonth;
                return (
                  <tr
                    key={r.year_month}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      isCurrent ? 'bg-blue-50/40 font-semibold' : ''
                    } ${varianceBg(r.variance_pct)}`}
                  >
                    <td className="px-4 py-2 font-mono text-xs">
                      {monthLabel(r.year_month)}
                      {isCurrent && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-blue-600">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right" data-testid={`target-cell-${r.year_month}`}>
                      {r.target_tons == null ? '—' : fmtTons(r.target_tons)}
                    </td>
                    <td className="px-4 py-2 text-right" data-testid={`actual-cell-${r.year_month}`}>
                      {fmtTons(r.actual_tons)}
                    </td>
                    <td className={`px-4 py-2 text-right ${varianceColor(r.variance_pct)}`}>
                      {r.variance_tons == null
                        ? '—'
                        : `${r.variance_tons >= 0 ? '+' : ''}${fmtTons(r.variance_tons)}`}
                    </td>
                    <td className={`px-4 py-2 text-right font-semibold ${varianceColor(r.variance_pct)}`}>
                      {fmtPct(r.variance_pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-100 text-slate-700 font-semibold">
              <tr>
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2 text-right">{fmtTons(summary?.totals?.target_tons)}</td>
                <td className="px-4 py-2 text-right">{fmtTons(summary?.totals?.actual_tons)}</td>
                <td className="px-4 py-2 text-right">
                  {summary?.totals?.target_tons
                    ? `${(summary.totals.actual_tons - summary.totals.target_tons).toFixed(2)} t`
                    : '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  {summary?.totals?.target_tons
                    ? `${((summary.totals.actual_tons / summary.totals.target_tons) * 100).toFixed(1)}%`
                    : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          Targets are managed by Super Approvers in <span className="font-semibold">Admin → Targets</span>.
        </p>
      </div>
    </div>
  );
}

function KpiTile({ icon, label, value, accent, testid }) {
  const map = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`rounded-xl p-4 border ${map[accent]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        {icon}
        {label}
      </div>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}

function LegendDot({ dot, label }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-600">
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export { MonthlyTargetReport };
export default MonthlyTargetReport;
